import { Button, ButtonGroup, Col, FloatingLabel, Form, Modal, Row, Spinner, Alert, Badge, Card } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useAddShowAfishaMutation, useDeleteShowAfishaMutation, useGetShowDetailsByIdQuery, usePutShowByIdMutation } from "../../../features/CallApi";
import { LoadScreenMin } from "../../UI/LoadScreenMin";
import { useEffect, useState, useCallback } from "react";
import 'react-quill/dist/quill.snow.css';
import { ReactQuillLC } from "../../UI/ReactQuillLC";

// Константы
const MAX_IMAGE_SIZE_MB = 2;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const BUTTON_TYPES = {
    SCHEDULE: "0",  // Изменено на строки
    LINK: "1",      // потому что select возвращает строку
    WAITING_LIST: "2"
};

// Компонент загрузки изображения
const ImageUploader = ({
    label,
    aspectRatio,
    currentImageUrl,
    isLoading,
    onImageChange,
    error
}) => (
    <Card className="mb-4 shadow-sm">
        <Card.Body>
            <Form.Label className="fw-semibold mb-2">{label}</Form.Label>

            <div className="image-preview-container mb-3" style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                minHeight: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
            }}>
                {isLoading ? (
                    <div className="text-center p-4">
                        <Spinner animation="border" size="sm" />
                        <p className="mt-2 text-muted small">Загрузка...</p>
                    </div>
                ) : (
                    <img
                        key={Date.now()}
                        src={currentImageUrl || '/NoPhoto.png'}
                        className="img-fluid"
                        alt={label}
                        style={{ maxHeight: '250px', objectFit: 'contain' }}
                    />
                )}
            </div>

            {aspectRatio && (
                <Alert variant="info" className="py-1 px-2 small">
                    <i className="bi bi-info-circle"></i> Рекомендованное соотношение: {aspectRatio}
                </Alert>
            )}

            <Form.Control
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={onImageChange}
                size="sm"
                isInvalid={!!error}
            />

            {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
        </Card.Body>
    </Card>
);

// Основной компонент
export const ShowEdit = ({ showEditForm, setShowEditForm, showId, setShowId, refetchShows }) => {
    const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm({
        defaultValues: {
            buttonType: "0" // Устанавливаем значение по умолчанию как строку
        }
    });

    const [hidden, setHidden] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const [valueEditor, setValueEditor] = useState('');
    const [valueEditorEn, setValueEditorEn] = useState('');

    const [imageStates, setImageStates] = useState({
        afisha: { isLoading: false, error: null },
        head: { isLoading: false, error: null },
        headWide: { isLoading: false, error: null },
        ticket: { isLoading: false, error: null }
    });

    const { data, isLoading: isLoadingData, refetch } = useGetShowDetailsByIdQuery(showId);
    const [putShowById, { isLoading: isPuting }] = usePutShowByIdMutation();
    const [addShowAfisha, { isLoading: postingImg }] = useAddShowAfishaMutation();
    const [deleteShowAfisha] = useDeleteShowAfishaMutation();

    const buttonTypeSelect = watch("buttonType"); // Возвращает строку: "0", "1" или "2"

    // Обработчик загрузки изображений
    const handleImageUpload = useCallback(async (event, imageBlock) => {
        const file = event.target.files[0];
        if (!file) return;

        setImageStates(prev => ({
            ...prev,
            [imageBlock]: { ...prev[imageBlock], isLoading: true, error: null }
        }));

        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            setImageStates(prev => ({
                ...prev,
                [imageBlock]: { ...prev[imageBlock], isLoading: false, error: `Максимальный размер фото ${MAX_IMAGE_SIZE_MB} Мб` }
            }));
            return;
        }

        try {
            const reader = new FileReader();
            reader.onloadend = async () => {
                const body = {
                    id: showId,
                    image: reader.result,
                    imageType: file.type
                };

                await addShowAfisha({ id: showId, imageBlock, ...body }).unwrap();
                await refetch();

                setImageStates(prev => ({
                    ...prev,
                    [imageBlock]: { ...prev[imageBlock], isLoading: false, error: null }
                }));
            };
            reader.readAsDataURL(file);
        } catch (error) {
            setImageStates(prev => ({
                ...prev,
                [imageBlock]: { ...prev[imageBlock], isLoading: false, error: "Ошибка загрузки изображения" }
            }));
        }
    }, [showId, addShowAfisha, refetch]);

    useEffect(() => {
        setIsLoading(isLoadingData || isPuting || postingImg);
    }, [isLoadingData, isPuting, postingImg]);

    useEffect(() => {
        if (data) {
            const fields = [
                "name", "nameEN", "ageCategory", "shortDescription", "quote", "quoteEN",
                "shortDescriptionEN", "linkVideo", "director", "directorEN", "duration",
                "durationEN", "priorityId", "buyButtonText", "buyButtonTextEN", "buyButtonLink",
                "waitListButtonEventText", "waitListButtonEventId"
            ];

            fields.forEach(field => setValue(field, data[field]));

            // Преобразуем buttonType в строку для select
            setValue("buttonType", String(data.buttonType || 0));

            setHidden(data.hidden);
            setValueEditor(data.description || '');
            setValueEditorEn(data.descriptionEN || '');
        }
    }, [data, setValue]);

    const onSubmit = useCallback(async (formData) => {
        console.log(formData)
        try {
            const submitData = {
                ...formData,
                id: showId,
                description: valueEditor,
                descriptionEN: valueEditorEn,
                buttonType: parseInt(formData.buttonType) // Преобразуем обратно в число для API
            };
            await putShowById(submitData).unwrap();
            await refetch();
            await refetchShows();
            handleClose(); // Закрываем форму после успешного сохранения
        } catch (error) {
            console.error("Ошибка сохранения:", error);
        }
    }, [showId, valueEditor, valueEditorEn, putShowById, refetch, refetchShows]);

    const handleClose = useCallback(() => {
        reset();
        setShowId(null);
        setShowEditForm(false);
    }, [reset, setShowId, setShowEditForm]);

    return (
        <Modal
            size="xl"
            show={showEditForm}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
            className="show-edit-modal"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>
                        <i className="bi bi-pencil-square me-2"></i>
                        Редактирование шоу
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {!isLoading ? (
                        <>
                            <div className="d-flex justify-content-end gap-2 mb-4">
                                <Button variant="outline-secondary" size="sm" onClick={handleClose}>
                                    <i className="bi bi-x-circle me-1"></i>Отменить
                                </Button>
                                <Button type="submit" variant="primary" size="sm">
                                    <i className="bi bi-check-circle me-1"></i>Сохранить
                                </Button>
                            </div>

                            {/* Основная информация */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-white fw-semibold">
                                    <i className="bi bi-info-circle me-2"></i>Основная информация
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <FloatingLabel label="Название шоу *" className="mb-3">
                                                <Form.Control
                                                    {...register("name", { required: "Поле не может быть пустым" })}
                                                    isInvalid={!!errors.name}
                                                    placeholder="Название шоу"
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.name?.message}</Form.Control.Feedback>
                                            </FloatingLabel>
                                        </Col>
                                        <Col md={6}>
                                            <FloatingLabel label="Название шоу (EN)" className="mb-3">
                                                <Form.Control {...register("nameEN")} placeholder="Название шоу EN" />
                                            </FloatingLabel>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <FloatingLabel label="Краткое описание" className="mb-3">
                                                <Form.Control {...register("shortDescription")} placeholder="Краткое описание" />
                                            </FloatingLabel>
                                        </Col>
                                        <Col md={6}>
                                            <FloatingLabel label="Краткое описание (EN)" className="mb-3">
                                                <Form.Control {...register("shortDescriptionEN")} placeholder="Краткое описание EN" />
                                            </FloatingLabel>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <FloatingLabel label="Цитата" className="mb-3">
                                                <Form.Control {...register("quote")} placeholder="Цитата" />
                                            </FloatingLabel>
                                        </Col>
                                        <Col md={6}>
                                            <FloatingLabel label="Цитата (EN)" className="mb-3">
                                                <Form.Control {...register("quoteEN")} placeholder="Цитата EN" />
                                            </FloatingLabel>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={3}>
                                            <FloatingLabel label="Возрастная категория" className="mb-3">
                                                <Form.Control type="number" {...register("ageCategory", { max: 18 })} placeholder="18+" />
                                            </FloatingLabel>
                                        </Col>
                                        <Col md={9}>
                                            <FloatingLabel label="Ссылка на видео" className="mb-3">
                                                <Form.Control {...register("linkVideo")} placeholder="https://..." />
                                            </FloatingLabel>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <FloatingLabel label="Режиссер" className="mb-3">
                                                <Form.Control {...register("director")} placeholder="Режиссер" />
                                            </FloatingLabel>
                                        </Col>
                                        <Col md={6}>
                                            <FloatingLabel label="Режиссер (EN)" className="mb-3">
                                                <Form.Control {...register("directorEN")} placeholder="Director" />
                                            </FloatingLabel>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <FloatingLabel label="Продолжительность" className="mb-3">
                                                <Form.Control {...register("duration")} placeholder="2 часа 30 минут" />
                                            </FloatingLabel>
                                        </Col>
                                        <Col md={6}>
                                            <FloatingLabel label="Продолжительность (EN)" className="mb-3">
                                                <Form.Control {...register("durationEN")} placeholder="2 hours 30 minutes" />
                                            </FloatingLabel>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={4}>
                                            <FloatingLabel label="Приоритет" className="mb-3">
                                                <Form.Control type="number" {...register("priorityId")} placeholder="0" />
                                            </FloatingLabel>
                                        </Col>
                                        <Col md={4}>
                                            <FloatingLabel label="Тип кнопки" className="mb-3">
                                                <Form.Select {...register("buttonType")} className="mb-3">
                                                    <option value={BUTTON_TYPES.SCHEDULE}>📅 Расписание</option>
                                                    <option value={BUTTON_TYPES.LINK}>🔗 Ссылка в кнопке</option>
                                                    <option value={BUTTON_TYPES.WAITING_LIST}>⏳ Лист ожидания</option>
                                                </Form.Select>
                                            </FloatingLabel>
                                        </Col>
                                        <Col md={4} className="d-flex align-items-center">
                                            <Form.Check
                                                type="switch"
                                                id="hidden-switch"
                                                label="Скрыть шоу"
                                                checked={hidden}
                                                {...register("hidden", { onChange: () => setHidden(!hidden) })}
                                                className="mt-2"
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Настройки кнопки (только для типа LINK) */}
                            {buttonTypeSelect === BUTTON_TYPES.LINK && (
                                <Card className="mb-4 shadow-sm">
                                    <Card.Header className="bg-white fw-semibold">
                                        <i className="bi bi-link-45deg me-2"></i>Настройки кнопки
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <FloatingLabel label="Текст кнопки *" className="mb-3">
                                                    <Form.Control
                                                        {...register("buyButtonText", { required: "Поле не может быть пустым" })}
                                                        isInvalid={!!errors.buyButtonText}
                                                        placeholder="Купить билет"
                                                    />
                                                    <Form.Control.Feedback type="invalid">{errors.buyButtonText?.message}</Form.Control.Feedback>
                                                </FloatingLabel>
                                            </Col>
                                            <Col md={6}>
                                                <FloatingLabel label="Текст кнопки (EN)" className="mb-3">
                                                    <Form.Control {...register("buyButtonTextEN")} placeholder="Buy ticket" />
                                                </FloatingLabel>
                                            </Col>
                                            <Col md={12}>
                                                <FloatingLabel label="Ссылка *" className="mb-3">
                                                    <Form.Control
                                                        {...register("buyButtonLink", { required: "Поле не может быть пустым" })}
                                                        isInvalid={!!errors.buyButtonLink}
                                                        placeholder="https://..."
                                                    />
                                                    <Form.Control.Feedback type="invalid">{errors.buyButtonLink?.message}</Form.Control.Feedback>
                                                </FloatingLabel>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            )}
                            {buttonTypeSelect === BUTTON_TYPES.WAITING_LIST && (
                                <Card className="mb-4 shadow-sm">
                                    <Card.Header className="bg-white fw-semibold">
                                        <i className="bi bi-link-45deg me-2"></i>Настройки кнопки
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <FloatingLabel label="Текст кнопки *" className="mb-3">
                                                    <Form.Control
                                                        {...register("waitListButtonEventText", { required: "Поле не может быть пустым" })}
                                                        isInvalid={!!errors.buyButtonText}
                                                        placeholder="Лист ожидания"
                                                    />
                                                    <Form.Control.Feedback type="invalid">{errors.buyButtonText?.message}</Form.Control.Feedback>
                                                </FloatingLabel>
                                            </Col>
                                            {/* <Col md={6}>
                                                <FloatingLabel label="Текст кнопки (EN)" className="mb-3">
                                                    <Form.Control {...register("buyButtonTextEN")} placeholder="Buy ticket" />
                                                </FloatingLabel>
                                            </Col> */}
                                            <Col md={12}>
                                                <FloatingLabel label="ID мероприятия в TP" className="mb-3">
                                                    <Form.Control
                                                        {...register("waitListButtonEventId", { required: "Поле не может быть пустым" })}
                                                        isInvalid={!!errors.buyButtonLink}
                                                        placeholder="368e7cbd-89f9-46d5-b273-2aa496ba67c5"
                                                    />
                                                    <Form.Control.Feedback type="invalid">{errors.buyButtonLink?.message}</Form.Control.Feedback>
                                                </FloatingLabel>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            )}
                            {/* Описания */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-white fw-semibold">
                                    <i className="bi bi-file-text me-2"></i>Описание
                                </Card.Header>
                                <Card.Body>
                                    <Form.Label className="fw-semibold">Описание шоу</Form.Label>
                                    <ReactQuillLC valueEditor={valueEditor} setValueEditor={setValueEditor} />

                                    <Form.Label className="fw-semibold mt-3">Описание шоу (EN)</Form.Label>
                                    <ReactQuillLC valueEditor={valueEditorEn} setValueEditor={setValueEditorEn} />
                                </Card.Body>
                            </Card>

                            {/* Изображения */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-white fw-semibold">
                                    <i className="bi bi-images me-2"></i>Изображения
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col lg={6}>
                                            <ImageUploader
                                                label="Афиша (2:3)"
                                                aspectRatio="2:3"
                                                currentImageUrl={data?.afishaImageType ? `/api/shows/afisha-img/${data.id}` : null}
                                                isLoading={imageStates.afisha.isLoading}
                                                onImageChange={(e) => handleImageUpload(e, "afisha")}
                                                error={imageStates.afisha.error}
                                            />
                                        </Col>
                                        <Col lg={6}>
                                            <ImageUploader
                                                label="Верхний блок (16:9)"
                                                aspectRatio="16:9"
                                                currentImageUrl={data?.headLayoutImageType ? `/api/shows/head-img/${data.id}` : null}
                                                isLoading={imageStates.head.isLoading}
                                                onImageChange={(e) => handleImageUpload(e, "head")}
                                                error={imageStates.head.error}
                                            />

                                            <ImageUploader
                                                label="Широкое изображение (1217:270)"
                                                aspectRatio="1217:270"
                                                currentImageUrl={data?.headLayoutWideImageType ? `/api/shows/head-wide-img/${data.id}` : null}
                                                isLoading={imageStates.headWide.isLoading}
                                                onImageChange={(e) => handleImageUpload(e, "headWide")}
                                                error={imageStates.headWide.error}
                                            />

                                            <ImageUploader
                                                label="Шаблон билета (240:113)"
                                                aspectRatio="240:113"
                                                currentImageUrl={data?.ticketImageType ? `/api/shows/ticekt-img/${data.id}?select=show` : null}
                                                isLoading={imageStates.ticket.isLoading}
                                                onImageChange={(e) => handleImageUpload(e, "ticket")}
                                                error={imageStates.ticket.error}
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </>
                    ) : (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Загрузка данных...</p>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="bg-light">
                    <Button variant="outline-secondary" onClick={handleClose}>
                        <i className="bi bi-x-circle me-1"></i>Отменить
                    </Button>
                    <Button type="submit" variant="primary" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Сохранение...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-check-circle me-1"></i>
                                Сохранить изменения
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};